import { Component, EventEmitter, Input, OnChanges, OnInit, AfterViewInit, Output, SimpleChanges, ViewChild, ElementRef, ViewChildren, QueryList, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { CommonModule, NgForOf, NgIf } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTreeModule, MatTreeNestedDataSource } from '@angular/material/tree';
import { NestedTreeControl } from '@angular/cdk/tree';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { FormsModule } from '@angular/forms';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { finalize, Subscription } from 'rxjs';
import { DataService, YearwiseResponse, Exam, Paper, Year } from '../../../services/data.service';
import { Router } from '@angular/router';
import { BundleUploadComponent } from '../../../components/bundle-upload/bundle-upload.component';
import { LoadingService } from '../../../services/loading.service';
import { FontSizeService, FontSizeOption } from '../../../services/font-size.service';

// Import the same node types as the navigation-app component
interface SubjectNode {
  name: string;
  children?: SubjectNode[];
  nameHindi?: string;
  id?: number;
  subjectId?: number;
  level?: number;
  type: 'subject' | 'section' | 'topic' | 'subtopic';
  isHighlighted?: boolean;
}

interface YearwiseNode {
  name: string;
  children?: YearwiseNode[];
  nameHindi?: string;
  id?: number;
  year_id?: number;
  level?: number;
  type: 'exam' | 'examType' | 'year';
  isHighlighted?: boolean;
}

interface TopicNode {
  name: string;
  children?: TopicNode[];
  id?: number;
  nameHindi?: string;
  level?: number;
  type: 'topic' | 'subtopic';
}

@Component({
  selector: 'app-single-tree',
  standalone: true,
  imports: [
    NgIf, NgForOf,
    MatToolbarModule, MatButtonModule, MatIconModule, MatMenuModule,
    MatTooltipModule, ScrollingModule, MatProgressSpinnerModule, MatTreeModule,
    FormsModule, MatSlideToggleModule, MatDialogModule,
    BundleUploadComponent
  ],
  templateUrl: './single-tree.component.html',
  styleUrls: ['./single-tree.component.css']
})
export class SingleTreeComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  // Inputs from parent component
  @Input() p1Mode: string = 'topicwise'; // Default mode
  @Input() p1Subjects: any[] = [];
  @Input() p1SubMap: any = {};
  @Input() p1YearwiseExams: any[] = [];
  @Input() p1YearwiseLower: any = {};
  @Input() hindiNameMap: {[key: string]: string} = {};
  @Input() isLoadingSubjects: boolean = false;
  @Input() isLoadingSections: boolean = false;
  @Input() searchTerm: string = ''; // Add search term input
  @Input() filterMode: boolean = false; // Add filter mode flag

  // Additional inputs for topics and subtopics
  @Input() p2TopicsMap: any = {};
  @Input() p2SubTopicsMap: any = {};
  @Input() isLoadingTopics: boolean = false;
  @Input() isLoadingSubtopics: boolean = false;
  @Input() topicIdMap: any = {};
  @Input() p2Years: {[key: string]: string[]} = {};
  @Input() isInternalUser: boolean = false; // Add input for internal user flag

  // Outputs to parent component
  @Output() modeChange = new EventEmitter<string>();
  @Output() subjectClick = new EventEmitter<any>();
  @Output() subItemClick = new EventEmitter<any>();
  @Output() topicClick = new EventEmitter<any>();
  @Output() subTopicClick = new EventEmitter<any>();
  @Output() yearwiseExamClick = new EventEmitter<any>();
  @Output() yearwiseLowerClick = new EventEmitter<any>();
  @Output() yearClick = new EventEmitter<any>();
  @Output() hindiNamesRequested = new EventEmitter<void>();
  @Output() topicsLoaded = new EventEmitter<any>();
  @Output() subtopicsLoaded = new EventEmitter<any>();
  @Output() languageChange = new EventEmitter<string>();
  @Output() searchChange = new EventEmitter<{term: string, filterMode: boolean}>();
  @Output() yearsListGenerated = new EventEmitter<string[]>();
  @Output() hierarchyPathChange = new EventEmitter<string[]>();
  @Output() showAllBundlesClick = new EventEmitter<void>();
  @Output() adminModeChange = new EventEmitter<boolean>(); // Add output for admin mode changes
  @Output() subjectsRefreshed = new EventEmitter<void>(); // Output for subject refresh
  @Output() fontSizeChange = new EventEmitter<FontSizeOption>(); // Output for font size changes
  @Output() blogModeChange = new EventEmitter<boolean>(); // Add output for blog mode changes
  @Output() changeNavViewChange = new EventEmitter<boolean>(); // Add output for change nav view changes

  // Tree controls and data sources
  treeControl = new NestedTreeControl<SubjectNode>(node => node.children);
  dataSource = new MatTreeNestedDataSource<SubjectNode>();
  treeData: SubjectNode[] = [];
  isTreeExpanded = true; // Start with expanded tree

  yearwiseTreeControl = new NestedTreeControl<YearwiseNode>(node => node.children);
  yearwiseDataSource = new MatTreeNestedDataSource<YearwiseNode>();
  yearwiseTreeData: YearwiseNode[] = [];
  isYearwiseTreeExpanded = true; // Start with expanded tree

  // Font size options
  fontSizeOptions = [
    { value: 'large' as FontSizeOption, label: 'Large' },
    { value: 'medium' as FontSizeOption, label: 'Medium' },
    { value: 'small' as FontSizeOption, label: 'Small' }
  ];
  currentFontSize: FontSizeOption = 'medium';
  private fontSizeSubscription: Subscription | null = null;

  // Track selected nodes
  selectedNode: SubjectNode | null = null;
  selectedYearwiseNode: YearwiseNode | null = null;

  // Track current selections
  selectedSubject: string | null = null;
  selectedSubItem: string | null = null;
  selectedTopic: string | null = null;
  selectedSubTopic: string | null = null;
  selectedYearwiseExam: string | null = null;
  selectedYearwiseLower: string | null = null;
  selectedYear: string | null = null;

  // Language settings
  selectedLanguage: string = 'englishonly'; // Default
  languageOptions = [
    { value: 'bilingual', label: 'Bilingual' },
    { value: 'englishonly', label: 'English Only' },
    { value: 'hindionly', label: 'Hindi Only' }
  ];

  // Search results tracking
  searchResults: string[] = [];

  // Search functionality properties
  isSearchExpanded = false;
  @ViewChild('searchInput') searchInput!: ElementRef;

  // Horizontal view toggle properties
  @Input() horizontalView: boolean = false;
  @Output() horizontalViewChange = new EventEmitter<boolean>();
  showHorizontalTabs = false;
  activeDropdownIndex: number | null = null;
  hoveredTabIndex: number | null = null;
  dropdownTimeout: any;

  // Add a reference to the language menu trigger
  @ViewChild(MatMenuTrigger) languageMenuTrigger!: MatMenuTrigger;

  // Array to store the path from root to selected node
  selectedNodePath: SubjectNode[] = [];
  selectedYearwiseNodePath: YearwiseNode[] = [];

  // Added for search debounce
  private searchTimeout: any;

  // Admin mode flag
  isAdminMode: boolean = false;

  // Flag to track if we've already logged the warning
  private hasLoggedNodeTypeWarning: boolean = false;

  // Blog mode toggle
  blogMode: boolean = false;

  // Change Nav View toggle (floating header mode)
  changeNavView: boolean = false;

  constructor(
    private dataService: DataService,
    private changeDetectorRef: ChangeDetectorRef,
    private dialog: MatDialog,
    private router: Router,
    private loadingService: LoadingService,
    private fontSizeService: FontSizeService
  ) {}

  ngOnInit(): void {
    // console.log('SingleTree - OnInit');
    // console.log('SingleTree - Input data:');
    // console.log('  p1Mode:', this.p1Mode);
    // console.log('  p1Subjects:', this.p1Subjects);
    // console.log('  p1SubMap:', this.p1SubMap);
    // console.log('  p1YearwiseExams:', this.p1YearwiseExams);
    // console.log('  p1YearwiseLower:', this.p1YearwiseLower);
    // console.log('  p2TopicsMap:', this.p2TopicsMap);
    // console.log('  p2SubTopicsMap:', this.p2SubTopicsMap);

    // Initialize selection paths
    this.selectedNodePath = [];
    this.selectedYearwiseNodePath = [];

    // Request Hindi names from parent component
    this.hindiNamesRequested.emit();

    // If in yearwise mode, fetch the yearwise data
    if (this.p1Mode === 'yearwise') {
      this.fetchYearwiseData();
    }

    // Set default tree expansion state to be expanded
    this.isTreeExpanded = true;
    this.isYearwiseTreeExpanded = true;

    // Initialize font size
    this.currentFontSize = this.fontSizeService.getCurrentFontSize();

    // Subscribe to font size changes
    this.fontSizeSubscription = this.fontSizeService.fontSize$.subscribe(size => {
      this.currentFontSize = size;
      this.changeDetectorRef.markForCheck();
    });

    // Check if data is available
    const hasTreeData = this.p1Subjects && this.p1Subjects.length > 0;

    if (hasTreeData && this.p1Mode === 'topicwise') {
      // We have data, update trees immediately
      this.updateTreeData();

      // Show all bundles when the tree is first loaded from API
      console.log('Tree data loaded from API - showing all bundles');
      this.showAllBundles();

      // Notify loading service that the component is fully loaded
      setTimeout(() => {
        this.loadingService.setSingleTreeLoaded();
        console.log('SingleTreeComponent: Notified loading service that component is fully loaded');
      }, 300);
    } else {
      // Even with no data, make sure to notify loading service to prevent preloader from sticking
      setTimeout(() => {
        this.loadingService.setSingleTreeLoaded();
        console.log('SingleTreeComponent: Notified loading service that component is marked as loaded (no data available yet)');
      }, 1000);
    }
  }

  ngOnDestroy(): void {
    // Clean up subscriptions
    if (this.fontSizeSubscription) {
      this.fontSizeSubscription.unsubscribe();
    }
  }

  // Font size methods
  setFontSize(size: FontSizeOption): void {
    this.fontSizeService.setFontSize(size);
    this.fontSizeChange.emit(size);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // console.log('SingleTree - OnChanges');

    if (changes['p1Subjects'] || changes['p1SubMap'] ||
        changes['p2TopicsMap'] || changes['p2SubTopicsMap']) {
      // console.log('SingleTree - Updating tree data due to input changes');
      this.updateTreeData();
    }

    if (changes['p1YearwiseExams'] || changes['p1YearwiseLower'] || changes['p2Years']) {
      // console.log('SingleTree - Updating yearwise tree data due to input changes');
      this.updateYearwiseTreeData();
    }

    // If search term changes, perform search
    if (changes['searchTerm']) {
      this.performSearch();
    }
  }

  ngAfterViewInit(): void {
    // After view init, ensure trees are expanded by default
    setTimeout(() => {
      if (this.p1Mode === 'topicwise' && this.treeData.length > 0) {
        this.expandAllNodes();
      } else if (this.p1Mode === 'yearwise' && this.yearwiseTreeData.length > 0) {
        this.expandAllYearwiseNodes();
      }

      // Always make sure loading service is notified that component is ready
      this.loadingService.setSingleTreeLoaded();
    }, 500);
  }

  // Update the topicwise tree data
  updateTreeData(): void {
    // console.log('SingleTree - Updating tree data with subjects:', this.p1Subjects);

    if (!this.p1Subjects || this.p1Subjects.length === 0) {
      console.warn('SingleTree - No subjects available to build tree');
      this.treeData = [];
      this.dataSource.data = [];

      // Add a retry mechanism for empty data
      if (window.innerWidth <= 768) { // Only retry on mobile view
        // console.log('SingleTree - In mobile view, scheduling retry for tree data');
        setTimeout(() => {
          // If data has arrived, update the tree
          if (this.p1Subjects && this.p1Subjects.length > 0) {
            // console.log('SingleTree - Retry successful, tree data now available:', this.p1Subjects);
            this.updateTreeData(); // Recursive call with data
            this.expandAllNodes(); // Make sure nodes are expanded
          } else {
            console.warn('SingleTree - Retry failed, tree data still unavailable');
          }
        }, 1000); // Wait a second before retrying
      }

      return;
    }

    // Convert flat arrays into tree structure with all levels
    this.treeData = this.p1Subjects.map(subject => {
      const node: SubjectNode = {
        name: subject,
        type: 'subject',
        children: []
      };

      // Add sections for this subject
      const sections = this.p1SubMap[subject] || [];
      if (sections && sections.length > 0) {
        node.children = sections.map((section: string) => {
          const sectionNode: SubjectNode = {
            name: section,
            type: 'section',
            children: []
          };

          // Add topics for this section
          const topics = this.p2TopicsMap[section] || [];
          if (topics && topics.length > 0) {
            sectionNode.children = topics.map((topic: string) => {
              const topicNode: SubjectNode = {
                name: topic,
                type: 'topic',
                children: []
              };

              // Add subtopics for this topic
              const subtopics = this.p2SubTopicsMap[topic] || [];
              if (subtopics && subtopics.length > 0) {
                topicNode.children = subtopics.map((subtopic: string) => {
                  return {
                    name: subtopic,
                    type: 'subtopic',
                    children: []
                  };
                });
              }

              return topicNode;
            });
          }

          return sectionNode;
        });
      }

      return node;
    });

    // console.log('SingleTree - Tree data updated with node count:', this.countNodes(this.treeData));

    // Initialize all nodes to ensure proper structure
    this.initializeTreeNodes(this.treeData);

    // Update the data source with the new tree data
    this.dataSource.data = this.treeData;

    // If search term exists, perform search to highlight nodes
    if (this.searchTerm) {
      this.performSearch();
    }

    // Always expand nodes after data is loaded
    setTimeout(() => {
      this.expandAllNodes();
      this.selectFirstCompletePath();
    }, 300);
  }

  private selectFirstCompletePath(): void {
    // Method disabled as per requirement - no auto selection of first subject/section
    console.log('Auto-selection of first path is disabled');
    return;
  }

  // Update the yearwise tree data
  updateYearwiseTreeData(): void {
    // console.log('SingleTree - Updating yearwise tree data with exams:', this.p1YearwiseExams);

    if (!this.p1YearwiseExams || this.p1YearwiseExams.length === 0) {
      console.warn('SingleTree - No yearwise exams available to build tree');
      this.yearwiseTreeData = [];
      this.yearwiseDataSource.data = [];
      return;
    }

    // Convert flat arrays into single unified tree structure with all levels
    this.yearwiseTreeData = this.p1YearwiseExams.map(exam => {
      const examNode: YearwiseNode = {
        name: exam,
        type: 'exam',
        children: []
      };

      // Add papers for this exam
      const papers = this.p1YearwiseLower[exam] || [];
      if (papers && papers.length > 0) {
        examNode.children = papers.map((paper: string) => {
          const paperNode: YearwiseNode = {
            name: paper,
            type: 'examType',
            children: []
          };

          // Add years for this paper
          const years = this.p2Years[paper] || [];
          if (years && years.length > 0) {
            paperNode.children = years.map((year: string) => {
              return {
                name: year,
                type: 'year'
              };
            });
          }

          return paperNode;
        });
      }

      return examNode;
    });

    // console.log('SingleTree - Yearwise tree data updated:', this.yearwiseTreeData);

    // Update the data source with the new yearwise tree data
    this.yearwiseDataSource.data = this.yearwiseTreeData;

    // Initialize the tree control's dataNodes explicitly
    this.yearwiseTreeControl.dataNodes = this.yearwiseTreeData;

    // Always expand all nodes immediately after data is loaded
    this.isYearwiseTreeExpanded = true;
    this.expandAllYearwiseNodes();

    // Remove auto-selection of first items
    // Previously, there was auto-selection code here that has been removed
  }

  onModeChange(mode: string): void {
    // console.log('SingleTree - Mode changed to:', mode);

    // Reset selection paths when changing modes
    this.selectedNodePath = [];
    this.selectedYearwiseNodePath = [];

    // Update mode
    this.p1Mode = mode;
    this.modeChange.emit(mode);

    // If switching to yearwise mode
    if (mode === 'yearwise') {
      // Reset all selections to force auto-selection
      this.selectedYearwiseExam = null;
      this.selectedYearwiseLower = null;
      this.selectedYear = null;

      // If no data is available, fetch it
      if (!this.p1YearwiseExams || this.p1YearwiseExams.length === 0) {
        this.fetchYearwiseData();
      } else {
        // Update tree data
        this.updateYearwiseTreeData();

        // Auto-select first items and expand all nodes immediately
        this.expandAllYearwiseNodes();
        this.autoSelectFirstItems();
        this.changeDetectorRef.detectChanges();
      }
    }
    // If switching to topicwise mode
    else if (mode === 'topicwise') {
      if (this.treeData.length > 0) {
        this.expandAllNodes();
      }
    }
  }

  /**
   * Recursively finds nodes that match the search term
   * @param nodes Node array to search through
   * @param searchTerm Term to search for
   * @param results Array to collect matching nodes
   * @returns true if any nodes matched
   */
  findMatchingNodes<T extends SubjectNode | YearwiseNode>(nodes: T[], searchTerm: string, results: any[]): boolean {
    if (!nodes || !searchTerm || searchTerm.length < 3 || nodes.length === 0) {
      return false;
    }

    const searchTermLower = searchTerm.toLowerCase();
    let hasMatch = false;

    for (const node of nodes) {
      // Reset highlight state for this node
      node.isHighlighted = false;

      // Check if this node's name matches
      const nodeName = node.name;
      const nodeNameLower = nodeName.toLowerCase();
      // Also check Hindi name if available through hindiNameMap
      const hindiName = this.hindiNameMap && this.hindiNameMap[nodeName] ? this.hindiNameMap[nodeName].toLowerCase() : '';
      let nodeMatches = nodeNameLower.includes(searchTermLower) || hindiName.includes(searchTermLower);

      // Check if any of the children match
      let childrenMatch = false;
      if (node.children && node.children.length > 0) {
        childrenMatch = this.findMatchingNodes<T>(node.children as T[], searchTerm, results);
      }

      // Mark this node as highlighted if it matches or any of its children match
      // Note: The actual text highlighting is now handled by the formatNodeName method
      // The isHighlighted property is still needed for filter mode and to expand parent nodes
      if (nodeMatches) {
        node.isHighlighted = true;
        results.push(node);
        // console.log(`Match found for "${searchTerm}" in node:`, nodeName);
        hasMatch = true;
      } else if (childrenMatch) {
        // If any children match, this node should be highlighted for the filter view
        node.isHighlighted = true;
        hasMatch = true;
        // console.log(`Node "${nodeName}" highlighted because children match`);
      }
    }

    return hasMatch;
  }

  /**
   * Performs the search operation across the tree data
   */
  performSearch(): void {
    if (!this.searchTerm || this.searchTerm.trim().length === 0) {
      return;
    }

    const term = this.searchTerm.trim().toLowerCase();
    
    // Reset highlights before starting new search
    this.resetHighlights(this.treeData);
    this.resetHighlights(this.yearwiseTreeData);

    // Store search results
    this.searchResults = [];

    // Determine which tree to search based on current mode
    if (this.p1Mode === 'topicwise') {
      // Search in topic tree
      this.findMatchingNodes(this.treeData, term, this.searchResults);
      
      // Apply filter if filter mode is active, otherwise just show with highlights
      this.dataSource.data = this.filterMode 
        ? this.filterTreeData(this.treeData, true) 
        : [...this.treeData];
        
      // Expand all nodes to show matches if in filter mode
      if (this.filterMode) {
        this.expandAllFilteredNodes(this.treeData, this.treeControl);
      } else {
        // Otherwise just expand nodes containing matches
        this.expandMatchedNodes(this.treeData, this.treeControl);
      }
    } else {
      // Search in yearwise tree
      this.findMatchingNodes(this.yearwiseTreeData, term, this.searchResults);
      
      // Apply filter if filter mode is active, otherwise just show with highlights
      this.yearwiseDataSource.data = this.filterMode 
        ? this.filterTreeData(this.yearwiseTreeData, true) 
        : [...this.yearwiseTreeData];
        
      // Expand nodes to show matches
      if (this.filterMode) {
        this.expandAllFilteredNodes(this.yearwiseTreeData, this.yearwiseTreeControl);
      } else {
        this.expandMatchedNodes(this.yearwiseTreeData, this.yearwiseTreeControl);
      }
    }

    // Emit search change event
    this.searchChange.emit({
      term: term,
      filterMode: this.filterMode
    });
  }

  // Reset highlights for all nodes
  resetHighlights<T extends SubjectNode | YearwiseNode>(nodes: T[]): void {
    if (!nodes || nodes.length === 0) return;

    for (const node of nodes) {
      node.isHighlighted = false;

      if (node.children && node.children.length > 0) {
        this.resetHighlights(node.children as T[]);
      }
    }
  }

  // Expand nodes that have matches
  expandMatchedNodes<T extends SubjectNode | YearwiseNode>(
      nodes: T[],
      treeControl: NestedTreeControl<T>
  ): void {
    if (!nodes || nodes.length === 0) return;

    for (const node of nodes) {
      // If this node or any of its children are highlighted, expand it
      if (node.isHighlighted && node.children && node.children.length > 0) {
        treeControl.expand(node);

        // Also expand its children that might be highlighted
        this.expandMatchedNodes(node.children as T[], treeControl);
      }
    }
  }

  // Format node name based on selected language and available Hindi names
  formatNodeName(englishName: string, hindiName?: string): string {
    try {
      if (!englishName) return '';

      // Helper function to escape HTML special characters
      const escapeHtml = (text: string): string => {
        if (!text) return '';
        return text
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');
      };

      // Helper function to highlight search terms in a text
      const highlightSearchTerms = (text: string): string => {
        if (!text || !this.searchTerm || !this.searchTerm.trim() || this.searchTerm.trim().length < 3) {
          return escapeHtml(text || '');
        }

        const searchTermLower = this.searchTerm.trim().toLowerCase();
        const textLower = text.toLowerCase();

        if (!textLower.includes(searchTermLower)) {
          return escapeHtml(text);
        }

        let result = '';
        let lastIndex = 0;
        let startIndex = textLower.indexOf(searchTermLower);

        while (startIndex !== -1) {
          // Add the text before the match
          result += escapeHtml(text.substring(lastIndex, startIndex));

          // Add the highlighted search term
          const matchedText = escapeHtml(text.substring(startIndex, startIndex + this.searchTerm.trim().length));
          result += `<span class="search-term-highlight">${matchedText}</span>`;

          // Move past this match
          lastIndex = startIndex + this.searchTerm.trim().length;

          // Look for next occurrence
          startIndex = textLower.indexOf(searchTermLower, lastIndex);
        }

        // Add any remaining text
        result += escapeHtml(text.substring(lastIndex));
        return result;
      };

      // Ensure we have a Hindi name to display
      const actualHindiName = hindiName || this.hindiNameMap[englishName] || '';

      // Normalize language to lowercase for consistent comparison
      const language = this.selectedLanguage.toLowerCase();

      // Format with highlighting based on language settings
      switch (language) {
        case 'bilingual':
          // Add proper classes and structure for bilingual mode
          const highlightedName = highlightSearchTerms(englishName);
          const highlightedHindiName = highlightSearchTerms(actualHindiName || englishName);
          return `<div class="bilingual-container"><div class="english-part">${highlightedName}</div><div class="hindi-part">${highlightedHindiName}</div></div>`;
        case 'englishonly':
          // Only show English name, never the Hindi name
          return `<div class="english-only">${highlightSearchTerms(englishName)}</div>`;
        case 'hindionly':
          // Use Hindi name if available, fallback to English
          return `<div class="hindi-only">${highlightSearchTerms(actualHindiName || englishName)}</div>`;
        default:
          return `<div class="english-only">${highlightSearchTerms(englishName)}</div>`;
      }
    } catch (error) {
      console.error('Error formatting node name:', error);
      return englishName; // Fallback to English name in case of errors
    }
  }

  onLanguageChange(language: string, event: Event): void {
    // console.log('SingleTree - Language changed to:', language);
    if (event) {
      event.stopPropagation();
    }

    console.log('SingleTree - Language changed to:', language);
    this.selectedLanguage = language;

    // If switching to Hindi mode but we have few or no Hindi names, request them
    if (this.selectedLanguage === 'hindionly' && Object.keys(this.hindiNameMap).length < 10) {
      this.hindiNamesRequested.emit();
    }

    // Close the menu programmatically
    if (this.languageMenuTrigger) {
      this.languageMenuTrigger.closeMenu();
    }

    // Apply language classes to container based on language selection
    const treeContainer = document.querySelector('.single-tree-container');
    if (treeContainer) {
      // First remove all language classes
      treeContainer.classList.remove('bilingual-mode', 'hindi-only-mode', 'english-only-mode');

      // Add appropriate class based on selection
      if (this.selectedLanguage === 'bilingual') {
        treeContainer.classList.add('bilingual-mode');
      } else if (this.selectedLanguage === 'hindionly') {
        treeContainer.classList.add('hindi-only-mode');
      } else {
        treeContainer.classList.add('english-only-mode');
      }
    }

    // Force refresh of tree displays to reflect language change
    this.updateTreeData();
    this.updateYearwiseTreeData();

    // Maintain expanded state by expanding previously expanded nodes
    setTimeout(() => {
      // For topicwise tree
      if (this.isTreeExpanded) {
        this.expandAllNodes();
      }
      // For yearwise tree
      if (this.isYearwiseTreeExpanded) {
        this.expandAllYearwiseNodes();
      }
    }, 100);

    // Emit language change event to parent component
    this.languageChange.emit(this.selectedLanguage);
  }

  toggleAllNodes(): void {
    if (this.isTreeExpanded) {
      this.collapseAllNodes();
    } else {
      this.expandAllNodes();
    }
  }

  // Expand all nodes in the tree - ensure complete expansion
  expandAllNodes(): void {
    console.log('Expanding all nodes in topicwise tree, nodes count:', this.treeData?.length || 0);
    this.isTreeExpanded = true;

    if (!this.treeData || this.treeData.length === 0) {
      console.warn('No tree data to expand');
      return;
    }

    try {
      // First ensure all nodes are properly initialized
      this.initializeTreeNodes(this.treeData);

      // Update data source after initialization
      this.dataSource.data = [...this.treeData];

      // Make sure all nodes are expanded for all levels
      setTimeout(() => {
        try {
          // First initialize the control's data nodes
          this.treeControl.dataNodes = this.treeData;

          // Then expand all nodes
          this.treeControl.expandAll();

          // Log the expansion state
          const expandedNodeCount = this.countExpandedNodes(this.treeData);
          console.log(`Expanded ${expandedNodeCount} nodes in the tree`);

          // Force UI update to ensure expanded state is reflected
          this.changeDetectorRef.detectChanges();
        } catch (error) {
          console.error('Error expanding nodes:', error);
        }
      }, 100);
    } catch (error) {
      console.error('Error initializing tree nodes for expansion:', error);
    }
  }

  // Helper method to count expanded nodes
  private countExpandedNodes(nodes: SubjectNode[]): number {
    if (!nodes || nodes.length === 0) return 0;

    let count = 0;
    nodes.forEach(node => {
      if (this.treeControl.isExpanded(node)) {
        count++;
        if (node.children && node.children.length > 0) {
          count += this.countExpandedNodes(node.children);
        }
      }
    });
    return count;
  }

  // Expand all nodes in the yearwise tree
  expandAllYearwiseNodes(): void {
    // console.log('SingleTree - Expanding all yearwise nodes');

    if (!this.yearwiseTreeData || this.yearwiseTreeData.length === 0) {
      return;
    }

    // Set the internal expanded flag to true
    this.isYearwiseTreeExpanded = true;

    // Use the tree control's expandAll method for better performance
    if (this.yearwiseTreeControl.dataNodes) {
      this.yearwiseTreeControl.expandAll();
    } else {
      // If dataNodes is not set, set it first
      this.yearwiseTreeControl.dataNodes = this.yearwiseTreeData;
      this.yearwiseTreeControl.expandAll();
    }

    // Force refresh the tree view immediately without setTimeout delay
    this.yearwiseDataSource.data = [...this.yearwiseTreeData];
    this.changeDetectorRef.detectChanges();
  }

  // Helper function to initialize tree nodes and ensure all levels are properly structured
  private initializeTreeNodes(nodes: SubjectNode[]): void {
    // Only log this warning once to avoid console spam
    if (!this.hasLoggedNodeTypeWarning) {
      // Warning about conflicting node types is internally triggered here
      // The Angular Material tree is using conflicting node types warning
      this.hasLoggedNodeTypeWarning = true;
    }

    if (!nodes) return;

    //console.log('SingleTree - Initializing tree nodes:', nodes.length);

    // Count of nodes by type for debugging
    const nodeTypes: {[key: string]: number} = {};

    nodes.forEach(node => {
      // Count node types
      nodeTypes[node.type] = (nodeTypes[node.type] || 0) + 1;

      // Ensure children array exists
      if (!node.children) {
        node.children = [];
      }

      // Handle different node types and ensure proper child loading
      if (node.type === 'subject') {
        // For subjects, ensure sections are loaded
        if (node.children.length === 0) {
          const sections = this.p1SubMap[node.name] || [];
          if (sections && sections.length > 0) {
            node.children = sections.map((section: string) => ({
              name: section,
              type: 'section' as const,
              children: []
            }));
          }
        }
      } else if (node.type === 'section') {
        // For sections, ensure topics are loaded
        if (node.children.length === 0) {
          const topics = this.p2TopicsMap[node.name] || [];
          if (topics && topics.length > 0) {
            node.children = topics.map((topic: string) => {
              const topicNode: SubjectNode = {
                name: topic,
                type: 'topic',
                children: []
              };
              return topicNode;
            });
          }
        }
      } else if (node.type === 'topic') {
        // For topics, ensure subtopics are loaded
        if (node.children.length === 0) {
          const subtopics = this.p2SubTopicsMap[node.name] || [];
          if (subtopics.length > 0) {
            node.children = subtopics.map((subtopic: string) => ({
              name: subtopic,
              type: 'subtopic' as const,
              children: []
            }));
          }
        }
      }

      // Special case for "Physical Division" with specific children like "Coastal Region"
      // and ensure "Coastal Region" and similar nodes have their children loaded
      if (node.name === 'Physical Division' ||
          node.name === 'Natural Region of India' ||
          node.name === 'Coastal Region') {

        console.log(`Processing special node: ${node.name}`);

        // For Physical Division, manually ensure its child nodes are properly structured
        if (node.name === 'Physical Division') {
          // Ensure children array exists
          if (!node.children) {
            node.children = [];
          }

          const hasCoastalRegion = node.children.some(child => child.name === 'Coastal Region');

          if (!hasCoastalRegion) {
            console.log('Adding Coastal Region to Physical Division');
            node.children.push({
              name: 'Coastal Region',
              type: 'section' as const,
              children: [
                {
                  name: 'Indian Coastline',
                  type: 'topic',
                  children: []
                },
                {
                  name: 'Eastern and Western Coast',
                  type: 'topic',
                  children: []
                }
              ]
            });
          }
        }

        // For Coastal Region, ensure it has its child nodes
        if (node.name === 'Coastal Region') {
          // Ensure children array exists
          if (!node.children) {
            node.children = [];
          }

          if (node.children.length === 0) {
            console.log('Adding children to Coastal Region');
            node.children = [
              {
                name: 'Indian Coastline',
                type: 'topic',
                children: []
              },
              {
                name: 'Eastern and Western Coast',
                type: 'topic',
                children: []
              }
            ];
          }
        }
      }

      // Recursively initialize child nodes
      if (node.children && node.children.length > 0) {
        this.initializeTreeNodes(node.children);
      }
    });

    //console.log('SingleTree - Node type counts:', nodeTypes);
  }

  // Collapse all nodes in the tree
  collapseAllNodes(): void {
    this.isTreeExpanded = false;
    if (this.treeData?.length) {
      this.treeControl.collapseAll();
    }
  }

  // Collapse all nodes in the yearwise tree
  collapseAllYearwiseNodes(): void {
    console.log('SingleTree - Collapsing all yearwise nodes');

    if (!this.yearwiseTreeData || this.yearwiseTreeData.length === 0) {
      return;
    }

    // Set the internal expanded flag to false
    this.isYearwiseTreeExpanded = false;

    // Recursively collapse all nodes
    const collapseRecursively = (nodes: YearwiseNode[]) => {
      if (!nodes) return;

      nodes.forEach(node => {
        this.yearwiseTreeControl.collapse(node);

        if (node.children && node.children.length > 0) {
          collapseRecursively(node.children);
        }
      });
    };

    collapseRecursively(this.yearwiseTreeData);
  }

  // Check if node has children for the mat-tree
  hasChild = (_: number, node: SubjectNode) => !!node.children && node.children.length > 0;
  hasYearwiseChild = (_: number, node: YearwiseNode) => !!node.children && node.children.length > 0;

  // Handle node selection in topicwise mode
  onNodeSelect(node: SubjectNode): void {
    console.log('SingleTree - Node selected:', node);

    // Find path to selected node
    const nodePath = this.findNodePath(this.treeData, node);
    this.selectedNodePath = nodePath;

    // Set current selections based on path
    this.selectedSubject = nodePath.length > 0 ? nodePath[0].name : null;
    this.selectedSubItem = nodePath.length > 1 ? nodePath[1].name : null;
    this.selectedTopic = nodePath.length > 2 ? nodePath[2].name : null;
    this.selectedSubTopic = nodePath.length > 3 ? nodePath[3].name : null;

    // Emit events based on the level of the selected node
    if (node.type === 'subject') {
      this.subjectClick.emit(node.name);
      console.log('Subject selected:', node.name);
    } else if (node.type === 'section') {
      this.subItemClick.emit(node.name);
      console.log('Section selected:', node.name);
    } else if (node.type === 'topic') {
      this.topicClick.emit(node.name);
      console.log('Topic selected:', node.name);
    } else if (node.type === 'subtopic') {
      this.subTopicClick.emit(node.name);
      console.log('Subtopic selected:', node.name);
    }

    // Update the hierarchy path (for navigation path)
    const hierarchyPath = nodePath.map(n => n.name);
    this.hierarchyPathChange.emit(hierarchyPath);

    // Store the selected node
    this.selectedNode = node;

    // Force refresh the tree to ensure correct styling is applied
    setTimeout(() => {
      // Create a shallow copy to force Angular change detection
      this.dataSource.data = [...this.treeData];
      this.changeDetectorRef.detectChanges();
      console.log('Tree data refreshed after node selection, path:', this.selectedNodePath.map(n => n.name));
    }, 0);
  }

  // Handle node selection in yearwise mode
  onYearwiseNodeSelect(node: YearwiseNode): void {
    // console.log('SingleTree - Yearwise node selected:', node);

    // Clear previous selected node path to reset highlighting
    this.selectedYearwiseNodePath = [];
    this.selectedYearwiseNode = null;

    // Find the path from root to this node
    this.selectedYearwiseNodePath = this.findYearwiseNodePath(this.yearwiseTreeData, node);

    this.selectedYearwiseNode = node;

    // Expand all parent nodes in the path
    if (this.selectedYearwiseNodePath.length > 1) {
      // Skip the last node (which is the selected node itself)
      for (let i = 0; i < this.selectedYearwiseNodePath.length - 1; i++) {
        const parentNode = this.selectedYearwiseNodePath[i];
        this.yearwiseTreeControl.expand(parentNode);
      }
    }

    if (node.type === 'exam') {
      this.selectedYearwiseExam = node.name;
      this.selectedYearwiseLower = null;
      this.selectedYear = null;
      this.yearwiseExamClick.emit(node.name);

      // Automatically expand the exam node to show its children (papers)
      if (node.children && node.children.length > 0) {
        this.yearwiseTreeControl.expand(node);
      }
    } else if (node.type === 'examType') {
      this.selectedYearwiseLower = node.name;
      this.selectedYear = null;
      this.yearwiseLowerClick.emit(node.name);
    } else if (node.type === 'year') {
      this.selectedYear = node.name;
      this.yearClick.emit(node.name);
    }

    // Emit the full hierarchy path to display in main-area's subheader
    const hierarchyPath = this.selectedYearwiseNodePath.map(n => n.name);
    if (this.selectedYear && !hierarchyPath.includes(this.selectedYear)) {
      hierarchyPath.push(this.selectedYear);
    }
    this.hierarchyPathChange.emit(hierarchyPath);

    // Force refresh the tree to ensure correct styling is applied
    setTimeout(() => {
      this.yearwiseDataSource.data = [...this.yearwiseTreeData];
      this.changeDetectorRef.detectChanges();
    }, 0);
  }

  // Filter tree data to only include highlighted nodes and their ancestors
  filterTreeData<T extends SubjectNode | YearwiseNode>(nodes: T[], isRoot: boolean = false): T[] {
    if (!nodes || nodes.length === 0) return [];

    // Clone the array to avoid modifying the original
    let result: T[] = [];

    for (const node of nodes) {
      // Always keep the root nodes in filter mode, even if not highlighted
      const isHighlighted = node.isHighlighted || isRoot;

      // Process children first to determine if this node should be included
      let filteredChildren: T[] = [];
      if (node.children && node.children.length > 0) {
        // Recursive call to filter children (not root level)
        filteredChildren = this.filterTreeData(node.children as T[], false);
      }

      // Include this node if it's highlighted or has highlighted children
      if (isHighlighted || filteredChildren.length > 0) {
        // Clone the node to avoid modifying the original
        const clonedNode = { ...node };

        // Set children to filtered children
        if (filteredChildren.length > 0) {
          // Use type assertion to handle the generic type constraint
          (clonedNode.children as T[]) = filteredChildren;
        } else if (node.children && node.children.length > 0) {
          // Keep empty children array if original had children
          clonedNode.children = [];
        }

        result.push(clonedNode as T);
      }
    }

    return result;
  }

  // Helper method to expand all nodes in filtered trees
  expandAllFilteredNodes<T>(nodes: T[], treeControl: NestedTreeControl<T>): void {
    if (!nodes || nodes.length === 0) return;

    nodes.forEach(node => {
      // Expand each parent node
      treeControl.expand(node);

      // Also expand children with expandable children
      if ((node as any).children && (node as any).children.length > 0) {
        this.expandAllFilteredNodes((node as any).children, treeControl);
      }
    });
  }

  /**
   * Toggles the search input visibility
   */
  toggleSearch(): void {
    this.isSearchExpanded = !this.isSearchExpanded;
    
    // If expanding, focus the input after a brief delay to allow animation
    if (this.isSearchExpanded) {
      setTimeout(() => {
        if (this.searchInput && this.searchInput.nativeElement) {
          this.searchInput.nativeElement.focus();
        }
      }, 300);
    } else {
      // If collapsing, clear the search if there was any
      if (this.searchTerm) {
        this.clearSearch();
      }
    }
  }

  /**
   * Clears the search input and results
   */
  clearSearch(): void {
    this.searchTerm = '';
    this.resetHighlights(this.treeData);
    this.resetHighlights(this.yearwiseTreeData);
    
    if (this.p1Mode === 'topicwise') {
      this.dataSource.data = [...this.treeData];
    } else {
      this.yearwiseDataSource.data = [...this.yearwiseTreeData];
    }
    
    // Reset any expansion that was done for search
    if (this.isTreeExpanded) {
      this.expandAllNodes();
    } else {
      this.collapseAllNodes();
    }
    
    // Emit empty search
    this.searchChange.emit({term: '', filterMode: this.filterMode});
    
    // Focus back on input if still expanded
    if (this.isSearchExpanded && this.searchInput && this.searchInput.nativeElement) {
      this.searchInput.nativeElement.focus();
    }
  }

  /**
   * Handles search input changes with debounce
   */
  onSearchInput(): void {
    // Clear any existing timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    
    // Set a new timeout to debounce the search
    this.searchTimeout = setTimeout(() => {
      if (this.searchTerm.trim().length > 0) {
        this.performSearch();
      } else if (this.searchTerm.trim().length === 0) {
        this.clearSearch();
      }
    }, 300); // 300ms debounce
  }

  /**
   * Toggles filter mode for search results
   */
  toggleFilterMode(event: any): void {
    this.filterMode = event.checked;
    
    // If we have an active search term, re-run the search with the new filter mode
    if (this.searchTerm && this.searchTerm.trim().length > 0) {
      this.performSearch();
    }
    
    // Emit the search change event
    this.searchChange.emit({term: this.searchTerm, filterMode: this.filterMode});
  }

  // Helper to count nodes in a tree for debugging
  private countNodes(nodes: SubjectNode[]): {total: number, byType: {[key: string]: number}} {
    if (!nodes) return {total: 0, byType: {}};

    const result = {
      total: 0,
      byType: {} as {[key: string]: number}
    };

    const countRecursively = (nodeArray: SubjectNode[]) => {
      if (!nodeArray) return;

      for (const node of nodeArray) {
        result.total++;
        result.byType[node.type] = (result.byType[node.type] || 0) + 1;

        if (node.children && node.children.length > 0) {
          countRecursively(node.children);
        }
      }
    };

    countRecursively(nodes);
    return result;
  }

  // Helper to ensure specific nodes are expanded
  private ensureNodeExpansion(nodeName: string): void {
    // Find the node in the tree data
    let nodeToExpand: SubjectNode | undefined;

    const findNode = (nodes: SubjectNode[]) => {
      for (const node of nodes) {
        if (node.name === nodeName) {
          nodeToExpand = node;
          return true;
        }
        if (node.children && node.children.length > 0) {
          if (findNode(node.children)) {
            return true;
          }
        }
      }
      return false;
    };

    findNode(this.treeData);

    if (nodeToExpand) {
      console.log(`Ensuring expansion of node: ${nodeName}`);
      this.treeControl.expand(nodeToExpand);

      // Also expand any children
      if (nodeToExpand.children && nodeToExpand.children.length > 0) {
        nodeToExpand.children.forEach(child => {
          this.treeControl.expand(child);
        });
      }
    }
  }

  selectNode(node: any): void {
    console.log('selectNode called with node:', node.name, 'type:', node.type);

    // Find the path from root to this node
    this.selectedNodePath = this.findNodePath(this.treeData, node);
    console.log('Selected node path:', this.selectedNodePath.map(n => n.name).join(' > '));

    // Set the selected node
    this.selectedNode = node;

    // Expand all parent nodes in the path
    if (this.selectedNodePath.length > 1) {
      // Skip the last node (which is the selected node itself)
      for (let i = 0; i < this.selectedNodePath.length - 1; i++) {
        const parentNode = this.selectedNodePath[i];
        this.treeControl.expand(parentNode);
      }
    }

    // Set selections based on node type
    if (node.type === 'subject') {
      this.selectedSubject = node.name;
      this.selectedSubItem = null;
      this.selectedTopic = null;
      this.selectedSubTopic = null;
    } else if (node.type === 'section') {
      // For sections, we need to set both subject and section
      if (this.selectedNodePath.length > 1) {
        this.selectedSubject = this.selectedNodePath[0].name;
      }
      this.selectedSubItem = node.name;
      this.selectedTopic = null;
      this.selectedSubTopic = null;
    } else if (node.type === 'topic') {
      // For topics, we need subject, section and topic
      if (this.selectedNodePath.length > 2) {
        this.selectedSubject = this.selectedNodePath[0].name;
        this.selectedSubItem = this.selectedNodePath[1].name;
      }
      this.selectedTopic = node.name;
      this.selectedSubTopic = null;
    } else if (node.type === 'subtopic') {
      // For subtopics, we need all levels
      if (this.selectedNodePath.length > 3) {
        this.selectedSubject = this.selectedNodePath[0].name;
        this.selectedSubItem = this.selectedNodePath[1].name;
        this.selectedTopic = this.selectedNodePath[2].name;
      }
      this.selectedSubTopic = node.name;
    }

    // Call onNodeSelect to handle event emissions
    this.onNodeSelect(node);

    // Emit the full hierarchy path to display in main-area's subheader
    const hierarchyPath = this.selectedNodePath.map(n => n.name);
    this.hierarchyPathChange.emit(hierarchyPath);

    // Force a complete refresh of the tree to ensure styling is applied correctly
    setTimeout(() => {
      this.dataSource.data = [...this.treeData];
      this.changeDetectorRef.detectChanges();
      console.log('Tree refreshed after selection. Current selections:', {
        subject: this.selectedSubject,
        section: this.selectedSubItem,
        topic: this.selectedTopic,
        subtopic: this.selectedSubTopic
      });
    }, 50);
  }

  // Find the path from root to the target node
  findNodePath(nodes: SubjectNode[], targetNode: SubjectNode, currentPath: SubjectNode[] = []): SubjectNode[] {
    if (!nodes || nodes.length === 0) return [];

    for (const node of nodes) {
      // Create a new path including the current node
      const newPath = [...currentPath, node];

      // If this is the target node, return the path
      if (node === targetNode) {
        return newPath;
      }

      // If this node has children, search them
      if (node.children && node.children.length > 0) {
        const foundPath = this.findNodePath(node.children, targetNode, newPath);
        if (foundPath.length > 0) {
          return foundPath;
        }
      }
    }

    // Node not found in this branch
    return [];
  }

  toggleNode(event: Event, node: any): void {
    event.stopPropagation();
    if (this.treeControl.isExpanded(node)) {
      this.treeControl.collapse(node);
    } else {
      this.treeControl.expand(node);
    }
  }

  // Fetch yearwise data from API
  fetchYearwiseData(): void {
    console.log('SingleTree - Fetching yearwise data');

    // If we already have data, don't fetch again
    if (this.p1YearwiseExams && this.p1YearwiseExams.length > 0) {
      console.log('SingleTree - Already have yearwise data, updating tree');
      this.updateYearwiseTreeData();

      // Show all bundles when the yearwise tree is first loaded
      console.log('Yearwise tree data loaded - showing all bundles');
      this.showAllBundles();

      // Notify loading service that the component is fully loaded
      setTimeout(() => {
        this.loadingService.setSingleTreeLoaded();
        console.log('SingleTreeComponent: Notified loading service that yearwise component is fully loaded');
      }, 300);
      return;
    }

    // If we need to fetch data from API
    console.log('SingleTree - Fetching yearwise data from API');

    this.dataService.getYearwiseData()
      .subscribe(
        (response: YearwiseResponse) => {
          console.log('SingleTree - Received yearwise data:', response);
          if (response && response.exams) {
            // Map the response to our data structure
            this.p1YearwiseExams = response.exams.map(exam => exam.exam_name);

            // Create the yearwise lower map (papers)
            this.p1YearwiseLower = {};
            this.p2Years = {}; // Reset years map

            response.exams.forEach((exam: Exam) => {
              // Store paper names for each exam
              this.p1YearwiseLower[exam.exam_name] = exam.papers.map(paper => paper.paper_name);

              // Store years for each paper
              exam.papers.forEach((paper: Paper) => {
                // Convert Year objects to strings and sort in descending order
                this.p2Years[paper.paper_name] = paper.years
                  .map(year => year.year.toString())
                  .sort((a, b) => parseInt(b) - parseInt(a));
              });
            });

            // Update the yearwise tree with the fetched data
            this.updateYearwiseTreeData();

            // Show all bundles when the yearwise tree is loaded from API
            console.log('Yearwise tree data loaded from API - showing all bundles');
            this.showAllBundles();

            // Notify loading service that the component is fully loaded
            setTimeout(() => {
              this.loadingService.setSingleTreeLoaded();
              console.log('SingleTreeComponent: Notified loading service that yearwise component is fully loaded');
            }, 300);
          }
        },
        (error: any) => {
          console.error('SingleTree - Error fetching yearwise data:', error);
        }
      );
  }

  // Helper method to auto-select first items
  private autoSelectFirstItems(): void {
    // Method disabled as per requirement - no auto selection of first bundle
    console.log('Auto-selection of first items is disabled');
    return;
  }

  // Toggle expand/collapse all yearwise nodes
  toggleAllYearwiseNodes(): void {
    // console.log('SingleTree - Toggling all yearwise nodes');
    if (this.isYearwiseTreeExpanded) {
      this.collapseAllYearwiseNodes();
      this.isYearwiseTreeExpanded = false;
    } else {
      this.expandAllYearwiseNodes();
      this.isYearwiseTreeExpanded = true;
    }
  }

  // Find the path from root to the target node for yearwise tree
  findYearwiseNodePath(nodes: YearwiseNode[], targetNode: YearwiseNode, currentPath: YearwiseNode[] = []): YearwiseNode[] {
    if (!nodes || nodes.length === 0) return [];

    for (const node of nodes) {
      // Create a new path including the current node
      const newPath = [...currentPath, node];

      // If this is the target node, return the path
      if (node === targetNode) {
        return newPath;
      }

      // If this node has children, search them
      if (node.children && node.children.length > 0) {
        const foundPath = this.findYearwiseNodePath(node.children, targetNode, newPath);
        if (foundPath.length > 0) {
          return foundPath;
        }
      }
    }

    // Node not found in this branch
    return [];
  }

  // Add the showAllBundles method to the component
  showAllBundles(): void {
    console.log('Show all bundles button clicked');

    // Reset selections
    this.selectedSubject = null;
    this.selectedSubItem = null;
    this.selectedTopic = null;
    this.selectedSubTopic = null;
    this.selectedNode = null;
    this.selectedNodePath = [];

    // Emit the event to show all bundles
    this.showAllBundlesClick.emit();

    // Reset UI selections (clear highlights in tree)
    if (this.treeData && this.treeData.length > 0) {
      this.resetHighlights(this.treeData);
      this.dataSource.data = [...this.treeData];
    }
  }

  // Toggle admin mode
  toggleAdminMode(): void {
    this.isAdminMode = !this.isAdminMode;
    console.log('Admin mode toggled:', this.isAdminMode);
    this.adminModeChange.emit(this.isAdminMode);
  }

  /**
   * Opens the upload bundle dialog
   */
  openUploadBundleDialog(): void {
    // Open the dialog instead of navigating
    this.dialog.open(BundleUploadComponent, {
      width: '1000px',
      maxWidth: '90vw',
      disableClose: false
    });
  }

  /**
   * Refreshes subjects by calling zero API and trees API
   */
  refreshSubjects(): void {
    console.log('Refreshing subjects...');

    // Show loading indicator
    this.isLoadingSubjects = true;

    // Reset selections
    this.selectedSubject = null;
    this.selectedSubItem = null;
    this.selectedTopic = null;
    this.selectedSubTopic = null;
    this.selectedNode = null;
    this.selectedNodePath = [];

    // First get subjects from zero level API
    this.dataService.getSubjectsFromZeroLevel().subscribe(
      (response) => {
        console.log('Received subjects from zero API:', response);

        if (response && response.topics) {
          // Process the response to update subjects
          this.updateTreeWithNewSubjects(response);

          // Fetch subject trees
          this.fetchSubjectTrees();
        } else {
          console.error('Invalid response from zero API');
          this.isLoadingSubjects = false;
        }
      },
      (error) => {
        console.error('Error refreshing subjects:', error);
        this.isLoadingSubjects = false;
      }
    );
  }

  /**
   * Updates the tree with new subjects from the API
   */
  private updateTreeWithNewSubjects(response: any): void {
    console.log('Updating tree with new subjects, count:', response.topics.length);

    // Update the p1Subjects array for parent component reference
    this.p1Subjects = response.topics.map((topic: any) => topic.name_eng);

    // Map the response to our tree data structure
    this.treeData = response.topics.map((topic: any) => {
      return {
        name: topic.name_eng,
        hindiName: topic.name_hindi || this.hindiNameMap[topic.name_eng],
        type: 'subject',
        id: topic.topic_id,
        subjectId: topic.topic_id,
        children: []
      };
    });

    // Update the data source
    this.dataSource.data = [...this.treeData];

    // Initialize the tree control's dataNodes
    this.treeControl.dataNodes = this.treeData;

    // Force change detection to update UI
    this.changeDetectorRef.detectChanges();
  }

  /**
   * Fetches subject trees after subjects are loaded
   */
  private fetchSubjectTrees(): void {
    if (!this.treeData || this.treeData.length === 0) {
      this.isLoadingSubjects = false;
      return;
    }

    // Get IDs of all subjects
    const subjectIds = this.treeData
      .filter(node => node.subjectId)
      .map(node => {
        // Convert to number safely, or return undefined if conversion fails
        const id = node.subjectId ? parseInt(String(node.subjectId)) : undefined;
        return !isNaN(id as number) ? id : undefined;
      })
      .filter((id): id is number => id !== undefined); // Type guard to ensure only numbers remain

    if (subjectIds.length === 0) {
      this.isLoadingSubjects = false;
      return;
    }

    // Call the API to get all topic trees
    this.dataService.getAllTopicTrees(subjectIds).subscribe(
      (response) => {
        console.log('Received subject trees:', response);

        if (response && response.trees) {
          // Process the trees and update the UI
          this.processTrees(response.trees);
        }

        // Hide loading indicator
        this.isLoadingSubjects = false;
      },
      (error) => {
        console.error('Error fetching subject trees:', error);
        this.isLoadingSubjects = false;
      }
    );
  }

  /**
   * Processes trees received from the API
   */
  private processTrees(trees: {[key: string]: any}): void {
    console.log('Processing trees, keys:', Object.keys(trees).length);

    if (!this.treeData || this.treeData.length === 0) {
      console.error('Tree data is empty, cannot process trees');
      this.isLoadingSubjects = false;
      return;
    }

    // Create a copy of tree data for safe manipulation
    const updatedTreeData = [...this.treeData];

    // For each subject in our tree data
    updatedTreeData.forEach(subject => {
      // Convert subjectId to string for tree lookup
      const subjectId = subject.subjectId ? String(subject.subjectId) : '';
      const tree = trees[subjectId];

      if (tree) {
        console.log(`Found tree for subject ${subject.name}, processing children...`);
        // Process the tree and create children
        subject.children = this.buildTreeFromNode(tree.children || []);
      } else {
        console.log(`No tree found for subject ${subject.name} with ID ${subjectId}`);
        subject.children = [];
      }
    });

    // Set up p1SubMap for section nodes to be visible
    this.p1SubMap = {};
    updatedTreeData.forEach(subject => {
      if (subject.children && subject.children.length > 0) {
        this.p1SubMap[subject.name] = subject.children.map(child => child.name);
      }
    });

    // Update the data source with the final tree data
    this.treeData = updatedTreeData;
    this.dataSource.data = this.treeData;

    // Initialize the tree control's dataNodes
    this.treeControl.dataNodes = this.treeData;

    // Make sure all nodes are expanded after processing
    setTimeout(() => {
      // Initialize tree nodes to ensure proper structure
      this.initializeTreeNodes(this.treeData);

      // Update data source again after initialization
      this.dataSource.data = [...this.treeData];

      // Expand all nodes
      this.expandAllNodes();

      // Force UI update
      this.changeDetectorRef.detectChanges();

      console.log('Tree refreshed and expanded');

      // Emit event to notify parent component
      this.subjectsRefreshed.emit();

      // Hide loading indicator
      this.isLoadingSubjects = false;
    }, 300);
  }

  /**
   * Builds tree structure from API response
   */
  private buildTreeFromNode(nodes: any[]): SubjectNode[] {
    if (!nodes || nodes.length === 0) return [];

    return nodes.map(node => {
      return {
        name: node.name_eng,
        hindiName: node.name_hindi || this.hindiNameMap[node.name_eng],
        type: this.getNodeType(node.item_level),
        id: node.topic_id,
        subjectId: node.topic_id,
        children: this.buildTreeFromNode(node.children || [])
      };
    });
  }

  /**
   * Determines node type based on level
   */
  private getNodeType(level: number): 'subject' | 'section' | 'topic' | 'subtopic' {
    switch (level) {
      case 0: return 'subject';
      case 1: return 'section';
      case 2: return 'topic';
      case 3:
      default: return 'subtopic';
    }
  }

  /**
   * Toggles between horizontal tabs view and vertical tree view
   */
  toggleHorizontalView(): void {
    this.showHorizontalTabs = !this.showHorizontalTabs;
    this.horizontalViewChange.emit(this.showHorizontalTabs);
    
    // Clear any active dropdowns when toggling
    this.activeDropdownIndex = null;
    this.hoveredTabIndex = null;
    
    // Close search if expanded when switching to horizontal view
    if (this.showHorizontalTabs && this.isSearchExpanded) {
      this.toggleSearch();
    }
  }

  /**
   * Handles mouse enter on a tab
   */
  onTabMouseEnter(index: number): void {
    if (this.dropdownTimeout) {
      clearTimeout(this.dropdownTimeout);
    }
    
    this.hoveredTabIndex = index;
    this.activeDropdownIndex = index;
  }

  /**
   * Handles mouse leave from a tab
   */
  onTabMouseLeave(): void {
    this.dropdownTimeout = setTimeout(() => {
      this.hoveredTabIndex = null;
      this.activeDropdownIndex = null;
    }, 300); // 300ms delay before closing
  }

  /**
   * Handles mouse enter on dropdown content
   */
  onDropdownMouseEnter(): void {
    if (this.dropdownTimeout) {
      clearTimeout(this.dropdownTimeout);
    }
  }

  /**
   * Handles mouse leave from dropdown content
   */
  onDropdownMouseLeave(): void {
    this.dropdownTimeout = setTimeout(() => {
      this.hoveredTabIndex = null;
      this.activeDropdownIndex = null;
    }, 300);
  }

  /**
   * Gets the root subjects for horizontal tabs
   */
  getSubjectsForTabs(): SubjectNode[] {
    return this.treeData.filter(node => node.type === 'subject');
  }

  /**
   * Gets children of a subject for dropdown content
   */
  getSubjectChildren(subject: SubjectNode): SubjectNode[] {
    return subject.children || [];
  }

  /**
   * Handles clicking on a tab item (subject or children)
   */
  onTabItemClick(node: SubjectNode): void {
    // Close dropdown
    this.activeDropdownIndex = null;
    this.hoveredTabIndex = null;
    
    // Select the node
    this.selectNode(node);
  }

  /**
   * Handle blog mode toggle
   */
  onBlogModeToggle(event: any): void {
    this.blogMode = event.checked;
    console.log('Blog mode toggled:', this.blogMode);
    this.blogModeChange.emit(this.blogMode);
  }

  onChangeNavViewToggle(event: any): void {
    this.changeNavView = event.checked;
    console.log('Change Nav View toggled:', this.changeNavView);
    this.changeNavViewChange.emit(this.changeNavView);
  }
}
